import React from "react";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { hasValidPhone, getPhoneWithCountryCode } from "../utils/phoneUtils";
import configCountries from "../../../Config/countries.json";

const formatDate = (dateVal) => {
    if (!dateVal) return null;
    if (typeof dateVal === "string") {
        return dateVal.split("T")[0];
    }
    return dateVal;
};

const MoreInfoModal = ({ show, onClose, user, onAction, t }) => {
    if (!show || !user) return null;

    const countryObj = user.Country
        ? configCountries.countries.find(
            (c) => c.value.toLowerCase() === user.Country.toLowerCase()
        )
        : null;

    const phoneWithCode = getPhoneWithCountryCode(user.Phone, user.Country);
    const hasPhone = hasValidPhone(user.Phone);

    const userTypeId = user.UserType ? user.UserType.Id : null;
    const isTeacher = userTypeId === 2 || userTypeId === 4;
    const isStudent = userTypeId === 5;
    const isAdmin = userTypeId === 1;

    let roleBadgeClass = "badge-secondary";
    if (isAdmin) roleBadgeClass = "badge-danger";
    else if (isTeacher) roleBadgeClass = "badge-info";
    else if (isStudent) roleBadgeClass = "badge-primary";

    const handleActionClick = (action) => {
        onAction(action, user);
    };

    return (
        <Modal size="lg" centered show={show} onHide={onClose} className="more-info-modal-container">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="w-100">
                    <div className="d-flex align-items-center">
                        <div className="more-info-icon-wrapper ml-3">
                            <i className="fad fa-user-chart"></i>
                        </div>
                        <div className="text-right">
                            <div className="h5 mb-1 font-weight-bold text-dark">{t("Pages.UsersControl.action.info")}</div>
                            <div className="small text-muted">{user.Name}</div>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3 pb-4">
                <div className="more-info-layout-wrapper">
                    {/* Main Panel: User Profile Hero & Card Grid */}
                    <div className="more-info-main-panel">

                        {/* Profile Hero Header */}
                        <div className="more-info-hero">
                            <div className="hero-avatar-section">
                                <div className="hero-avatar-wrapper position-relative">
                                    <LazyLoadImage
                                        src={user.Photo || "/assets/images/logo/130p150.png"}
                                        alt={user.Name}
                                        className={`hero-avatar-img ${user.IsActive ? "border-active" : "border-inactive"}`}
                                        title={user.IsActive ? t("Pages.UsersControl.Active") : t("Pages.UsersControl.NotActive")}
                                    />
                                    {user.ActivateUntilDate && user.ActivateUntilDate !== "0" && (
                                        <span className="hero-crown-badge" title={t("Pages.Profile.ActivateUntilDate.title") + ": " + user.ActivateUntilDate}>
                                            <i className="fa-solid fa-crown text-warning"></i>
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="hero-details-section">
                                <div className="d-flex align-items-center flex-wrap gap-2 justify-content-start">
                                    <h4 className="hero-name mb-0 font-weight-bold text-dark">{user.Name}</h4>
                                    {user.Sex && (
                                        <i
                                            className={`fas fa-${user.Sex === "female" ? "venus text-danger" : "mars text-primary"} hero-sex-icon mr-2`}
                                            title={t('Pages.Register.Sex.' + user.Sex)}
                                            style={{ fontSize: '1rem' }}
                                        ></i>
                                    )}
                                </div>
                                <div className="hero-meta text-muted mt-2 d-flex align-items-center flex-wrap gap-2">
                                    <span className="hero-username">@{user.Username}</span>
                                    <span className="hero-divider text-muted mx-1">•</span>
                                    <span className={`badge ${roleBadgeClass} hero-role-badge px-2 py-1`}>
                                        {user.UserType?.Name || t("NoInfo")}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Grid Cards */}
                        <div className="more-info-grid-modern mt-4">
                            {/* Role / User Type */}
                            <div className="info-detail-card-modern">
                                <div className="info-detail-icon-modern text-info">
                                    <i className="fad fa-user-shield"></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.WhatsApp.userType")}</span>
                                    <span className="info-detail-value-modern">
                                        {user.UserType?.Name || t("NoInfo")}
                                    </span>
                                </div>
                            </div>

                            {/* School */}
                            {user.School && userTypeId !== 1 && userTypeId !== 6 && (
                                <div className="info-detail-card-modern">
                                    <div className="info-detail-icon-modern text-purple" style={{ color: 'var(--brand-purple)' }}>
                                        <i className="fad fa-school"></i>
                                    </div>
                                    <div className="info-detail-content-modern">
                                        <span className="info-detail-label-modern">{t("Pages.WhatsApp.school")}</span>
                                        <span className="info-detail-value-modern">
                                            {user.School?.Name || t("NoInfo")}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Class Room (For Students) */}
                            {userTypeId === 5 && (
                                <div className="info-detail-card-modern">
                                    <div className="info-detail-icon-modern text-primary" style={{ color: '#0ea5e9' }}>
                                        <i className="fad fa-chalkboard-teacher"></i>
                                    </div>
                                    <div className="info-detail-content-modern">
                                        <span className="info-detail-label-modern">{t("Pages.Profile.ClassRoom.title") || "الصف الدراسي"}</span>
                                        <span className="info-detail-value-modern">
                                            {user.ClassRoom || t("NoInfo")}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Email */}
                            <div className="info-detail-card-modern">
                                <div className="info-detail-icon-modern text-primary">
                                    <i className="fad fa-envelope-open-text"></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.Register.Email.title")}</span>
                                    <span className="info-detail-value-modern">
                                        {user.Email ? (
                                            <a href={`mailto:${user.Email}`} className="text-decoration-none text-dark hover-primary-link">
                                                {user.Email}
                                            </a>
                                        ) : (
                                            t("NoInfo")
                                        )}
                                    </span>
                                </div>
                            </div>

                            {/* Phone */}
                            <div className="info-detail-card-modern">
                                <div className="info-detail-icon-modern text-success">
                                    <i className="fad fa-phone-alt"></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.Register.Phone.title")}</span>
                                    <span className="info-detail-value-modern" dir="ltr" style={{ textAlign: 'right' }}>
                                        {user.Phone ? (
                                            <a href={`tel:${user.Phone}`} className="text-decoration-none text-dark hover-success-link">
                                                {user.Phone}
                                            </a>
                                        ) : (
                                            t("NoInfo")
                                        )}
                                    </span>
                                </div>
                            </div>

                            {/* Gender (Sex) */}
                            <div className="info-detail-card-modern">
                                <div className="info-detail-icon-modern text-warning">
                                    <i className={`fad fa-${user.Sex === "female" ? "venus text-danger" : "mars text-primary"}`}></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.Register.Sex.title")}</span>
                                    <span className="info-detail-value-modern">
                                        {user.Sex ? (
                                            <>
                                                <i className={`fas fa-${user.Sex === "female" ? "venus text-danger" : "mars text-primary"} ml-1`}></i>
                                                {t('Pages.Register.Sex.' + user.Sex)}
                                            </>
                                        ) : (
                                            t("NoInfo")
                                        )}
                                    </span>
                                </div>
                            </div>

                            {/* Birthday */}
                            <div className="info-detail-card-modern">
                                <div className="info-detail-icon-modern text-danger">
                                    <i className="fad fa-birthday-cake"></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.Register.Birthday.title")}</span>
                                    <span className="info-detail-value-modern">
                                        {formatDate(user.Birthday) || t("NoInfo")}
                                    </span>
                                </div>
                            </div>

                            {/* Status / Activation */}
                            <div className="info-detail-card-modern">
                                <div className="info-detail-icon-modern text-success">
                                    <i className="fad fa-shield-check"></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.Profile.ActivateUntilDate.title")}</span>
                                    <span className="info-detail-value-modern">
                                        {user.ActivateUntilDate !== "0" ? (
                                            <span className="text-success font-weight-bold">
                                                <i className="fas fa-calendar-alt ml-1"></i>
                                                {user.ActivateUntilDate}
                                            </span>
                                        ) : (
                                            <span className="text-danger font-weight-bold">
                                                {t('Pages.UsersControl.NotActive')}
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>

                            {/* Activation Code */}
                            <div className="info-detail-card-modern">
                                <div className="info-detail-icon-modern text-warning">
                                    <i className="fad fa-key"></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.Profile.ActivateCode.title") || "كود التفعيل"}</span>
                                    <span className="info-detail-value-modern" style={{ fontWeight: '600', letterSpacing: '0.5px' }}>
                                        {user.ActivateCode || t("NoInfo")}
                                    </span>
                                </div>
                            </div>

                            {/* Last Login */}
                            <div className="info-detail-card-modern">
                                <div className="info-detail-icon-modern text-primary">
                                    <i className="fad fa-sign-in-alt"></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.Profile.LastLogin.title")}</span>
                                    <span className="info-detail-value-modern">{user.LastLogin || t("NoInfo")}</span>
                                </div>
                            </div>

                            {/* Country */}
                            <div className="info-detail-card-modern">
                                <div className="info-detail-icon-modern text-info">
                                    <i className="fad fa-globe-asia"></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.UsersControl.advancedSearch.Country.title")}</span>
                                    <span className="info-detail-value-modern d-flex align-items-center gap-1 justify-content-start">
                                        {countryObj ? (
                                            <div className="large-flag-hover-wrapper" title={`${countryObj.label}${user.Address ? ' - ' + user.Address : ''}`}>
                                                <LazyLoadImage
                                                    src={process.env.PUBLIC_URL + countryObj.icon}
                                                    alt={countryObj.label}
                                                    className="user-country-flag-large ml-1"
                                                />
                                                <span>{countryObj.label}</span>
                                            </div>
                                        ) : (
                                            user.Country || t("NoInfo")
                                        )}
                                    </span>
                                </div>
                            </div>

                            {/* Address */}
                            <div className="info-detail-card-modern">
                                <div className="info-detail-icon-modern text-warning">
                                    <i className="fad fa-map-marker-alt"></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.Register.Address.title") || "العنوان"}</span>
                                    <span className="info-detail-value-modern">{user.Address || t("NoInfo")}</span>
                                </div>
                            </div>

                            {/* Creation Date */}
                            <div className="info-detail-card-modern">
                                <div className="info-detail-icon-modern text-secondary">
                                    <i className="fad fa-calendar-plus"></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.Lessons.CreationDate")}</span>
                                    <span className="info-detail-value-modern">{user.CreationDate || t("NoInfo")}</span>
                                </div>
                            </div>

                            {/* Update Date */}
                            <div className="info-detail-card-modern">
                                <div className="info-detail-icon-modern text-secondary">
                                    <i className="fad fa-calendar-edit"></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.Lessons.UpdateDate")}</span>
                                    <span className="info-detail-value-modern">{user.UpdateDate || t("NoInfo")}</span>
                                </div>
                            </div>

                            {/* Relative Parent (for sub-user, userTypeId === 6) */}
                            {userTypeId === 6 && user.Parent && (
                                <div
                                    className="info-detail-card-modern clickable-parent-card"
                                    onClick={() => onAction("moreInfo", user.Parent)}
                                >
                                    <div className="info-detail-icon-modern text-primary">
                                        <i className="fad fa-user-friends"></i>
                                    </div>
                                    <div className="info-detail-content-modern">
                                        <span className="info-detail-label-modern">{t("Pages.UsersControl.relativeParent")}</span>
                                        <span className="info-detail-value-modern text-primary hover-underline">
                                            {user.Parent.Name}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Relative Sub-Users (for parent/subscriber, userTypeId === 2) */}
                            {userTypeId === 2 && user.SubUsers && user.SubUsers.length > 0 && (
                                <div className="info-detail-card-modern" style={{ gridColumn: 'span 2' }}>
                                    <div className="info-detail-icon-modern text-info">
                                        <i className="fad fa-users"></i>
                                    </div>
                                    <div className="info-detail-content-modern w-100">
                                        <span className="info-detail-label-modern">{t("Pages.UsersControl.relativeSubUsers")}</span>
                                        <div className="d-flex flex-wrap gap-2 mt-1 justify-content-start">
                                            {user.SubUsers.map(subUser => (
                                                <span
                                                    key={subUser.Id}
                                                    className="badge badge-light p-2 border hover-primary-badge"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onAction("moreInfo", subUser);
                                                    }}
                                                    style={{ cursor: 'pointer', borderRadius: '8px' }}
                                                >
                                                    <i className={`fas fa-${subUser.Sex === "female" ? "venus text-danger" : "mars text-primary"} ml-1`}></i>
                                                    {subUser.Name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Status Flags */}
                        {(user.IsSuperTeacher || user.IsForTest || user.IsLocked || !user.IsActive) && (
                            <div className="more-info-flags-card-modern mt-3">
                                <div className="info-detail-icon-modern text-muted">
                                    <i className="fad fa-tags"></i>
                                </div>
                                <div className="info-detail-content-modern">
                                    <span className="info-detail-label-modern">{t("Pages.UsersControl.advancedSearch.status")}</span>
                                    <div className="d-flex flex-wrap gap-2 mt-1 justify-content-start">
                                        {user.IsSuperTeacher && (
                                            <span className="badge badge-success p-2">
                                                <i className="fas fa-award ml-1"></i>
                                                {t("Pages.Register.IsSuperTeacher") || "صلاحيّات مركز"}
                                            </span>
                                        )}
                                        {user.IsForTest && (
                                            <span className="badge badge-info p-2">
                                                <i className="fas fa-vial ml-1"></i>
                                                {t("Pages.UsersControl.IsForTest") || "الحساب تجريبي"}
                                            </span>
                                        )}
                                        {user.IsLocked && (
                                            <span className="badge badge-dark p-2">
                                                <i className="fas fa-lock ml-1"></i>
                                                {t("Pages.UsersControl.Locked")}
                                            </span>
                                        )}
                                        {!user.IsActive && (
                                            <span className="badge badge-danger p-2">
                                                <i className="fas fa-user-slash ml-1"></i>
                                                {t("Pages.UsersControl.NotActive")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar: Administrative Actions Menu */}
                    <div className="more-info-sidebar-panel">
                        <div className="sidebar-section-header-modern">
                            <i className="fad fa-sliders-h ml-2 text-primary"></i>
                            <span>{t("Pages.UsersControl.actionsMenu") || "إجراءات التحكم والخيارات"}</span>
                        </div>

                        <div className="sidebar-actions-scroll scrollbar-custom">
                            {user.IsDeleted ? (
                                <div className="sidebar-group-modern">
                                    <div className="sidebar-group-title-modern text-success">{t("Pages.UsersControl.IsDeleted")}</div>
                                    <button type="button" className="sidebar-action-btn-modern success-btn" onClick={() => handleActionClick("restore")}>
                                        <i className="far fa-trash-undo-alt icon ml-2"></i>
                                        <span>{t("Pages.UsersControl.action.undo")}</span>
                                    </button>
                                    <button type="button" className="sidebar-action-btn-modern danger-btn mt-2" onClick={() => handleActionClick("fire")}>
                                        <i className="far fa-trash icon text-danger ml-2"></i>
                                        <span>{t("Pages.UsersControl.action.remove")}</span>
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Primary Actions Group */}
                                    <div className="sidebar-group-modern">
                                        <div className="sidebar-group-title-modern">{t("Pages.UsersControl.advancedSearch.personalSection") || "بيانات الحساب"}</div>

                                        {userTypeId !== 7 && (
                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("edit")}>
                                                <i className="far fa-edit icon text-info ml-2"></i>
                                                <span>{t("Pages.UsersControl.action.edit")}</span>
                                            </button>
                                        )}

                                        {hasPhone && (
                                            <Link
                                                to={`/ControlPanel/WhatsApp/${phoneWithCode}`}
                                                className="sidebar-action-btn-modern text-decoration-none"
                                                onClick={onClose}
                                            >
                                                <i className="fab fa-whatsapp icon text-success ml-2"></i>
                                                <span>{t("Pages.UsersControl.communication.whatsapp") || "واتساب"}</span>
                                            </Link>
                                        )}

                                        {hasPhone && (
                                            <Link
                                                to={`/ControlPanel/SMS/${phoneWithCode}`}
                                                className="sidebar-action-btn-modern text-decoration-none"
                                                onClick={onClose}
                                            >
                                                <i className="fal fa-comment-alt-lines icon text-info ml-2"></i>
                                                <span>{t("Pages.UsersControl.communication.sms") || "رسالة SMS"}</span>
                                            </Link>
                                        )}

                                        <button type="button" className="sidebar-action-btn-modern d-flex align-items-center justify-content-between" onClick={() => handleActionClick("notes")}>
                                            <span className="d-flex align-items-center">
                                                <i className={`far fa-sticky-note icon ml-2 ${user.notesCount > 0 ? "text-danger" : "text-primary"}`}></i>
                                                <span>{t("Pages.UsersControl.action.notes")}</span>
                                            </span>
                                            {user.notesCount > 0 && (
                                                <span className="badge badge-danger px-2 py-1" style={{ borderRadius: '10px', fontSize: '0.75rem' }}>
                                                    {user.notesCount}
                                                </span>
                                            )}
                                        </button>

                                        {userTypeId !== 7 && (
                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("change_password")}>
                                                <i className="far fa-key icon text-secondary ml-2"></i>
                                                <span>{t("Pages.UsersControl.action.change_password")}</span>
                                            </button>
                                        )}

                                        {userTypeId !== 1 && userTypeId !== 7 && (
                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("changeLoginInfo")}>
                                                <i className="far fa-fingerprint icon text-success ml-2"></i>
                                                <span>{t("Pages.UsersControl.action.changeLoginInfo")}</span>
                                            </button>
                                        )}

                                        {userTypeId === 5 && (
                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("move")}>
                                                <i className="far fa-exchange icon text-warning ml-2"></i>
                                                <span>{t("Pages.StudentInClassRoomsControl.action.move")}</span>
                                            </button>
                                        )}

                                        {(userTypeId === 2 || userTypeId === 7) && (
                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("bills")}>
                                                <i className="far fa-file-invoice-dollar icon text-info ml-2"></i>
                                                <span>{t("Pages.UsersControl.action.bills")}</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* Reports & Logs Group */}
                                    <div className="sidebar-group-modern">
                                        <div className="sidebar-group-title-modern">{t("Pages.UsersControl.export.category.activity") || "السجلات والتقارير"}</div>

                                        <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("lessonMarks")}>
                                            <i className="far fa-graduation-cap icon text-warning ml-2"></i>
                                            <span>{t("Pages.UsersControl.action.lessonMarks") || "علامات الدروس والمهام"}</span>
                                        </button>

                                        <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("lessonViews")}>
                                            <i className="far fa-book-open icon text-primary ml-2"></i>
                                            <span>{t("Pages.UsersControl.action.lessonViews") || "سجل مشاهدة الدروس"}</span>
                                        </button>

                                        <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("downloadFiles")}>
                                            <i className="far fa-download icon text-success ml-2"></i>
                                            <span>{t("Pages.UsersControl.action.downloadFiles") || "الملفات المحملة"}</span>
                                        </button>

                                        <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("errorReports")}>
                                            <i className="far fa-bug icon text-danger ml-2"></i>
                                            <span>{t("Pages.UsersControl.action.errorReports") || "بلاغات الأخطاء"}</span>
                                        </button>

                                        <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("contactMessages")}>
                                            <i className="far fa-envelope icon text-info ml-2"></i>
                                            <span>{t("Pages.UsersControl.action.contactMessages") || "رسائل اتصل بنا"}</span>
                                        </button>
                                    </div>

                                    {/* Permissions & Operations */}
                                    <div className="sidebar-group-modern">
                                        <div className="sidebar-group-title-modern">{t("Pages.UsersControl.export.category.account") || "الصلاحيات والإدارة"}</div>

                                        {userTypeId === 2 && (
                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("testUser")}>
                                                <i className="fad fa-alarm-exclamation icon text-danger ml-2"></i>
                                                <span>{t("Pages.UsersControl.action.testUser")}</span>
                                            </button>
                                        )}

                                        {userTypeId === 3 && (
                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("changePermission")}>
                                                <i className="fad fa-water-lower icon text-danger ml-2"></i>
                                                <span>{t("Pages.UsersControl.action.makeSchoolTeacher")}</span>
                                            </button>
                                        )}

                                        {(userTypeId === 3 || userTypeId === 4) && (
                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("accessToAllClass")}>
                                                <i className="fa-solid fa-users-between-lines icon text-primary ml-2"></i>
                                                <span>{t("Pages.UsersControl.action.accessToAllClass")}</span>
                                            </button>
                                        )}

                                        {userTypeId === 4 && (
                                            <>
                                                <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("changePermission")}>
                                                    <i className="fad fa-water-rise icon text-danger ml-2"></i>
                                                    <span>{t("Pages.UsersControl.action.makeSchoolManager")}</span>
                                                </button>
                                                <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("showChangeSchool")}>
                                                    <i className="far fa-school icon text-primary ml-2"></i>
                                                    <span>{t("Pages.UsersControl.action.changeSchool")}</span>
                                                </button>
                                                <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("changeUserType")}>
                                                    <i className="far fa-user-tag icon text-warning ml-2"></i>
                                                    <span>{t("Pages.UsersControl.action.changeTypeToUser")}</span>
                                                </button>
                                            </>
                                        )}

                                        {userTypeId === 2 && (
                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("changeUserType")}>
                                                <i className="far fa-chalkboard-teacher icon text-warning ml-2"></i>
                                                <span>{t("Pages.UsersControl.action.changeTypeToSchoolTeacher")}</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* Advanced & Destructive */}
                                    <div className="sidebar-group-modern">
                                        <div className="sidebar-group-title-modern">{t("Pages.UsersControl.actionsMenu") || "خيارات متقدمة"}</div>

                                        {userTypeId !== 7 && (
                                            <button type="button" className="sidebar-action-btn-modern" onClick={() => handleActionClick("useAs")}>
                                                <i className="fad fa-alien icon text-warning ml-2"></i>
                                                <span>{t("Pages.UsersControl.action.useAs") + " " + user.Name.split(" ")[0]}</span>
                                            </button>
                                        )}

                                        {userTypeId !== 7 && (
                                            <button type="button" className="sidebar-action-btn-modern danger-zone-btn mt-2" onClick={() => handleActionClick("remove")}>
                                                <i className="far fa-trash icon text-danger ml-2"></i>
                                                <span>{t("Pages.UsersControl.action.remove")}</span>
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 text-center justify-content-center">
                <button type="button" className="btn btn-light px-5 py-2" style={{ borderRadius: '10px', fontWeight: '600' }} onClick={onClose}>
                    {t("Close") || "إغلاق"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default MoreInfoModal;
